import { Box, Flex, HStack, VStack } from "@chakra-ui/react";
import CDS from "design-system";
import { ActionSummary } from "features/Resources/Shared/models";
import { MeetingSummary } from "features/Resources/Meeting/models";
import { dateInFormat } from "helpers/time";

interface CompactMeetingRowProps {
  action: ActionSummary;
  onClick?: () => void;
}

function getMeetingAlert(meeting: MeetingSummary): { text: string; color: string } | null {
  if (meeting.status === "pending_scheduling") {
    return { text: "Needs scheduling", color: "orange.500" };
  }

  const attendees = meeting.attendees ?? [];
  if (attendees.length === 0) return null;

  const declined = attendees.filter((a) => a.responseStatus === "declined");
  if (declined.length > 0) {
    return { text: `${declined.length} declined`, color: "red.500" };
  }

  const pending = attendees.filter((a) => a.responseStatus === "pending");
  if (pending.length > 0 && pending.length === attendees.length) {
    return { text: "No responses", color: "orange.500" };
  }

  return null;
}

export default function CompactMeetingRow({ action, onClick }: CompactMeetingRowProps) {
  const meeting = action.action as MeetingSummary;
  const date = action.unifiedDate;
  const isPending = meeting.status === "pending_scheduling";
  const alert = getMeetingAlert(meeting);

  return (
    <Flex
      align="center"
      gap="3"
      py="6px"
      px={2}
      cursor="pointer"
      _hover={{ bg: "gray.50" }}
      onClick={onClick}
      borderBottom="1px solid"
      borderColor="gray.100"
    >
      {/* Date block */}
      {date && !isPending ? (
        <VStack spacing="0" minW="36px" bg="gray.50" borderRadius="md" px="1.5" py="1" align="center">
          <CDS.Text10 fontSize="2xs" color="content.tertiary" textTransform="uppercase" lineHeight="1.2">
            {dateInFormat(date, "MMM")}
          </CDS.Text10>
          <CDS.Text12 fontWeight="600" color="content.primary" lineHeight="1.2">
            {dateInFormat(date, "D")}
          </CDS.Text12>
        </VStack>
      ) : (
        <Box minW="36px" bg="orange.50" borderRadius="md" px="1.5" py="1.5" textAlign="center">
          <CDS.Text10 fontSize="2xs" color="orange.500" fontWeight="600">
            TBD
          </CDS.Text10>
        </Box>
      )}

      {/* Content */}
      <Box flex="1" minW={0}>
        <CDS.Text14 fontSize="13px" color="gray.700" noOfLines={1}>
          {meeting.name}
        </CDS.Text14>
        <HStack spacing="1" mt="0.5">
          {date && !isPending ? (
            <>
              <CDS.Text10 color="content.tertiary">
                {dateInFormat(date, "h:mm A")}
              </CDS.Text10>
              {meeting.durationMinutes && (
                <CDS.Text10 color="content.tertiary">
                  · {meeting.durationMinutes}m
                </CDS.Text10>
              )}
            </>
          ) : (
            <CDS.Text10 color="orange.500">
              Needs scheduling
            </CDS.Text10>
          )}
        </HStack>
      </Box>

      {/* Alert */}
      {alert && !isPending && (
        <CDS.Text10 color={alert.color} fontWeight="500" flexShrink={0}>
          {alert.text}
        </CDS.Text10>
      )}
    </Flex>
  );
}
